/**
 * Farm Management API Service
 * 
 * This service provides methods for interacting with farm management
 * backend endpoints including farms, fields, irrigation sectors, pump stations,
 * and weather stations.
 */

import apiClient from './apiClient';
import type { FarmWeatherPageResponse, RainfallSummary, EtoComparisonResponse } from '@/types/weatherPage';
import type {
  Farm,
  FarmCreate,
  FarmUpdate,
  FarmListResponse,
  FarmOption,
  FarmStatsResponse,
  WeatherData,
  WeatherDataResponse,
  Field,
  FieldCreate,
  FieldUpdate,
  FieldListResponse,
  FieldGeometryResponse,
  FieldSummaryResponse,
  IrrigationSector,
  IrrigationSectorCreate,
  IrrigationSectorUpdate,
  IrrigationSectorListResponse,
  IrrigationSectorGeometryResponse,
  IrrigationSectorSummaryResponse,
  IrrigationRequirementRequest,
  IrrigationRequirementResponse,
  PumpStation,
  PumpStationCreate,
  PumpStationUpdate,
  PumpStationListResponse,
  WeatherStation,
  WeatherStationCreate,
  WeatherStationUpdate,
  WeatherStationListResponse,
  GeoImportResult,
  DripSectorDefaults,
  SprinkleSectorDefaults,
  FarmSectorDefaults,
} from '@/types/farms';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Build query string from parameters
 */
const buildQueryString = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

// ============================================================================
// Farm API
// ============================================================================

/**
 * Get all farms for the current user
 */
export const getFarms = async (params?: {
  page?: number;
  per_page?: number;
  is_active?: boolean;
}): Promise<FarmListResponse> => {
  const queryString = params ? buildQueryString(params) : '';
  return apiClient.get<FarmListResponse>(`/farms${queryString}`);
};

/**
 * Lightweight farm list for dropdowns/selectors. Returns the entire
 * accessible set in a single unpaginated payload — id, name, timezone,
 * coordinates, is_active. No user roles.
 */
export const getFarmOptions = async (params?: {
  include_inactive?: boolean;
}): Promise<FarmOption[]> => {
  const queryString = params ? buildQueryString(params) : '';
  return apiClient.get<FarmOption[]>(`/farms/list${queryString}`);
};

/**
 * Get a specific farm by ID
 */
export const getFarm = async (farmId: string): Promise<Farm> => {
  return apiClient.get<Farm>(`/farms/${farmId}`);
};

/**
 * Create a new farm
 */
export const createFarm = async (data: FarmCreate): Promise<Farm> => {
  return apiClient.post<Farm>('/farms', data);
};

/**
 * Update an existing farm
 */
export const updateFarm = async (
  farmId: string,
  data: FarmUpdate
): Promise<Farm> => {
  return apiClient.put<Farm>(`/farms/${farmId}`, data);
};

/**
 * Delete a farm
 */
export const deleteFarm = async (farmId: string): Promise<{ success: boolean }> => {
  return apiClient.delete<{ success: boolean }>(`/farms/${farmId}`);
};

/**
 * Hard-discard an in-progress wizard draft farm (removes the farm and ALL its
 * children). The backend refuses this for non-draft farms.
 */
export const discardDraftFarm = async (farmId: string): Promise<void> => {
  return apiClient.delete<void>(`/farms/${farmId}/draft`);
};

/**
 * Get farm statistics
 */
export const getFarmStats = async (): Promise<FarmStatsResponse> => {
  return apiClient.get<FarmStatsResponse>('/farms/stats');
};

/**
 * Get weather data for a farm
 */
export const getFarmWeather = async (farmId: string): Promise<WeatherDataResponse> => {
  return apiClient.get<WeatherDataResponse>(`/farms/${farmId}/weather`);
};

/**
 * Get aggregated weather page data for a farm
 */
export const getFarmWeatherPage = async (
  farmId: string,
  params?: {
    forecast_days?: number;
    history_days?: number;
    history_start_date?: string;
    history_end_date?: string;
    station_id?: number;
  }
): Promise<FarmWeatherPageResponse> => {
  const queryString = params ? buildQueryString(params) : '';
  return apiClient.get<FarmWeatherPageResponse>(`/farms/${farmId}/weather/page${queryString}`);
};

export const getFarmEtoComparison = async (
  farmId: string,
  days = 30,
): Promise<EtoComparisonResponse> => {
  return apiClient.get<EtoComparisonResponse>(
    `/farms/${farmId}/weather/eto-comparison?days=${days}`,
  );
};

/**
 * Get rainfall summary (selected period, hydrological year, irrigation season).
 * Returns null when the farm has no weather station configured.
 */
export const getFarmRainfallSummary = async (
  farmId: string,
  params?: {
    period_start?: string;  // YYYY-MM-DD
    period_end?: string;    // YYYY-MM-DD (inclusive)
    station_id?: number;
    prefer_local?: boolean;
  }
): Promise<RainfallSummary | null> => {
  const queryString = params ? buildQueryString(params) : '';
  return apiClient.get<RainfallSummary | null>(
    `/farms/${farmId}/weather/rainfall-summary${queryString}`,
  );
};

/**
 * Get all available timezones
 */
export const getAllTimezones = async (): Promise<string[]> => {
  return apiClient.get<string[]>('/farms/timezones/all');
};

/**
 * Search timezones by query
 */
export const searchTimezones = async (query: string, limit: number = 20): Promise<string[]> => {
  const queryString = buildQueryString({ q: query, limit });
  return apiClient.get<string[]>(`/farms/timezones/search${queryString}`);
};

/**
 * Export farm data as GeoPackage
 */
export const exportFarmGeoPackage = async (
  farmId: string,
  options?: { language?: string; includeInactive?: boolean }
): Promise<Blob> => {
  const authHeaders = apiClient.getAuthHeaders();
  const params = new URLSearchParams();
  if (options?.language) params.append('language', options.language);
  if (options?.includeInactive !== undefined) params.append('include_inactive', String(options.includeInactive));
  const queryString = params.toString();
  
  const response = await fetch(
    `${import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'}/farms/${farmId}/export/geopackage${queryString ? `?${queryString}` : ''}`,
    {
      method: 'GET',
      headers: authHeaders,
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.blob();
};

// ============================================================================
// Farm Sector Defaults API
// ============================================================================

/**
 * Get farm sector defaults
 */
export const getFarmSectorDefaults = async (farmId: string): Promise<FarmSectorDefaults> => {
  return apiClient.get<FarmSectorDefaults>(`/farms/${farmId}/sector-defaults`);
};

/**
 * Update farm sector defaults
 */
export const updateFarmSectorDefaults = async (
  farmId: string,
  defaults: Partial<FarmSectorDefaults>
): Promise<FarmSectorDefaults> => {
  return apiClient.put<FarmSectorDefaults>(`/farms/${farmId}/sector-defaults`, defaults);
};

// ============================================================================
// Field API
// ============================================================================

/**
 * Get all fields for a farm
 */
export const getFields = async (params: {
  farm_id: string;
  page?: number;
  size?: number;
}): Promise<FieldListResponse> => {
  const { farm_id, page, size } = params;
  const queryParams = new URLSearchParams();
  if (page !== undefined) queryParams.append('skip', String((page - 1) * (size || 100)));
  if (size !== undefined) queryParams.append('limit', String(size));
  const queryString = queryParams.toString();
  return apiClient.get<FieldListResponse>(`/fields/farm/${farm_id}${queryString ? `?${queryString}` : ''}`);
};

/**
 * Get a specific field by ID
 */
export const getField = async (fieldId: string): Promise<Field> => {
  return apiClient.get<Field>(`/fields/${fieldId}`);
};

/**
 * Create a new field
 */
export const createField = async (data: FieldCreate): Promise<Field> => {
  console.log('🔍 [DIAGNOSIS] farmApi.createField - Data being sent to API:', {
    fullData: data,
    crop_species_id: data.crop_species_id,
    crop_species_id_type: typeof data.crop_species_id,
    crop_variety_id: data.crop_variety_id,
    crop_variety_id_type: typeof data.crop_variety_id,
    hasSpeciesIdKey: 'crop_species_id' in data,
    hasVarietyIdKey: 'crop_variety_id' in data,
    willBeStringified: JSON.stringify(data),
  });
  return apiClient.post<Field>('/fields', data);
};

/**
 * Update an existing field
 */
export const updateField = async (
  fieldId: string,
  data: FieldUpdate
): Promise<Field> => {
  console.log('🔍 [DIAGNOSIS] farmApi.updateField - Data being sent to API:', {
    fullData: data,
    crop_species_id: data.crop_species_id,
    crop_species_id_type: typeof data.crop_species_id,
    crop_variety_id: data.crop_variety_id,
    crop_variety_id_type: typeof data.crop_variety_id,
    hasSpeciesIdKey: 'crop_species_id' in data,
    hasVarietyIdKey: 'crop_variety_id' in data,
    willBeStringified: JSON.stringify(data),
  });
  return apiClient.put<Field>(`/fields/${fieldId}`, data);
};

/**
 * Delete a field
 */
export const deleteField = async (fieldId: string): Promise<{ success: boolean }> => {
  return apiClient.delete<{ success: boolean }>(`/fields/${fieldId}`);
};

/**
 * Get field geometry
 */
export const getFieldGeometry = async (fieldId: string): Promise<FieldGeometryResponse> => {
  return apiClient.get<FieldGeometryResponse>(`/fields/${fieldId}/geometry`);
};

/**
 * Get field summary
 */
export const getFieldSummary = async (fieldId: string): Promise<FieldSummaryResponse> => {
  return apiClient.get<FieldSummaryResponse>(`/fields/${fieldId}/summary`);
};

// ============================================================================
// Irrigation Sector API
// ============================================================================

/**
 * Get all irrigation sectors for a field
 */
export const getIrrigationSectors = async (params: {
  field_id: string;
  page?: number;
  size?: number;
}): Promise<IrrigationSectorListResponse> => {
  const { field_id, page, size } = params;
  const queryParams = new URLSearchParams();
  if (page !== undefined) queryParams.append('skip', String((page - 1) * (size || 100)));
  if (size !== undefined) queryParams.append('limit', String(size));
  const queryString = queryParams.toString();
  console.log(`🔍 [IRRIGATION_SECTOR_DEBUG] getIrrigationSectors called with field_id: ${field_id}`);
  const response = await apiClient.get<IrrigationSectorListResponse>(
    `/irrigation-sectors/field/${field_id}${queryString ? `?${queryString}` : ''}`
  );
  console.log(`🔍 [IRRIGATION_SECTOR_DEBUG] Response received:`, response);
  console.log(`🔍 [IRRIGATION_SECTOR_DEBUG] Items count: ${response.items?.length || 0}`);
  console.log(`🔍 [IRRIGATION_SECTOR_DEBUG] First item:`, response.items?.[0]);
  return response;
};

/**
 * Get all irrigation sectors for a farm
 */
export const getIrrigationSectorsByFarm = async (params: {
  farm_id: string;
  page?: number;
  size?: number;
}): Promise<IrrigationSectorListResponse> => {
  const { farm_id, page, size } = params;
  const queryParams = new URLSearchParams();
  if (page !== undefined) queryParams.append('skip', String((page - 1) * (size || 100)));
  if (size !== undefined) queryParams.append('limit', String(size));
  const queryString = queryParams.toString();
  console.log(`🔍 [IRRIGATION_SECTOR_DEBUG] getIrrigationSectorsByFarm called with farm_id: ${farm_id}`);
  const response = await apiClient.get<IrrigationSectorListResponse>(
    `/irrigation-sectors/farm/${farm_id}${queryString ? `?${queryString}` : ''}`
  );
  console.log(`🔍 [IRRIGATION_SECTOR_DEBUG] Response received:`, response);
  console.log(`🔍 [IRRIGATION_SECTOR_DEBUG] Items count: ${response.items?.length || 0}`);
  return response;
};

/**
 * Get a specific irrigation sector by ID
 */
export const getIrrigationSector = async (sectorId: string): Promise<IrrigationSector> => {
  return apiClient.get<IrrigationSector>(`/irrigation-sectors/${sectorId}`);
};

/**
 * PR-UX3: minimal valve + device info for a sector (used by the map's
 * sector panel to render manual valve controls). Returns null fields when
 * the sector has no linked valve / its device has been deleted.
 */
export interface SectorValveInfo {
  sector_id: number;
  sector_name: string;
  valve_id: string | null;
  valve_number: number | null;
  valve_enabled: boolean | null;
  device_id: number | null;
  device_eui: string | null;
  device_name: string | null;
  device_last_seen: string | null;
}

export const getSectorValveInfo = async (sectorId: number): Promise<SectorValveInfo> => {
  return apiClient.get<SectorValveInfo>(`/irrigation-sectors/${sectorId}/valve-info`);
};

/**
 * Create a new irrigation sector
 */
export const createIrrigationSector = async (data: IrrigationSectorCreate): Promise<IrrigationSector> => {
  return apiClient.post<IrrigationSector>('/irrigation-sectors', data);
};

/**
 * Update an existing irrigation sector
 */
export const updateIrrigationSector = async (
  sectorId: string,
  data: IrrigationSectorUpdate
): Promise<IrrigationSector> => {
  return apiClient.put<IrrigationSector>(`/irrigation-sectors/${sectorId}`, data);
};

/**
 * Delete an irrigation sector
 */
export const deleteIrrigationSector = async (sectorId: string): Promise<{ success: boolean }> => {
  return apiClient.delete<{ success: boolean }>(`/irrigation-sectors/${sectorId}`);
};

/**
 * Get irrigation sector geometry
 */
export const getIrrigationSectorGeometry = async (sectorId: string): Promise<IrrigationSectorGeometryResponse> => {
  return apiClient.get<IrrigationSectorGeometryResponse>(`/irrigation-sectors/${sectorId}/geometry`);
};

/**
 * Calculate irrigation requirement for a sector
 */
export const calculateIrrigationRequirement = async (
  sectorId: string,
  data: IrrigationRequirementRequest
): Promise<IrrigationRequirementResponse> => {
  return apiClient.post<IrrigationRequirementResponse>(
    `/irrigation-sectors/${sectorId}/calculate-irrigation`,
    data
  );
};

// ============================================================================
// Pump Station API
// ============================================================================

/**
 * Get all pump stations for a farm
 */
export const getPumpStations = async (params: {
  farm_id: string;
  page?: number;
  size?: number;
}): Promise<PumpStationListResponse> => {
  // Backend lists pump stations under /pump-stations/farm/{farm_id}
  // (there is no /pump-stations?farm_id= route).
  const { farm_id, ...rest } = params;
  const queryString = buildQueryString(rest);
  return apiClient.get<PumpStationListResponse>(
    `/pump-stations/farm/${farm_id}${queryString}`,
  );
};

/**
 * Get a specific pump station by ID
 */
export const getPumpStation = async (stationId: string): Promise<PumpStation> => {
  return apiClient.get<PumpStation>(`/pump-stations/${stationId}`);
};

/**
 * Create a new pump station
 */
export const createPumpStation = async (data: PumpStationCreate): Promise<PumpStation> => {
  return apiClient.post<PumpStation>('/pump-stations', data);
};

/**
 * Update an existing pump station
 */
export const updatePumpStation = async (
  stationId: string,
  data: PumpStationUpdate
): Promise<PumpStation> => {
  return apiClient.put<PumpStation>(`/pump-stations/${stationId}`, data);
};

/**
 * Delete a pump station
 */
export const deletePumpStation = async (stationId: string): Promise<{ success: boolean }> => {
  return apiClient.delete<{ success: boolean }>(`/pump-stations/${stationId}`);
};

// ============================================================================
// Weather Station API
// ============================================================================

/**
 * Get all weather stations for a farm
 */
export const getWeatherStations = async (params: {
  farm_id: string;
  page?: number;
  size?: number;
}): Promise<WeatherStationListResponse> => {
  const queryString = buildQueryString(params);
  return apiClient.get<WeatherStationListResponse>(`/weather-stations${queryString}`);
};

/**
 * Get all weather stations for a farm via the by-farm path endpoint
 * (`GET /weather-stations/farm/{farm_id}`). Use this rather than the query-param
 * variant above — the backend only exposes the path route.
 */
export const getWeatherStationsByFarm = async (
  farmId: string,
): Promise<WeatherStationListResponse> => {
  return apiClient.get<WeatherStationListResponse>(`/weather-stations/farm/${farmId}`);
};

/**
 * Get a specific weather station by ID
 */
export const getWeatherStation = async (stationId: string): Promise<WeatherStation> => {
  return apiClient.get<WeatherStation>(`/weather-stations/${stationId}`);
};

/**
 * Create a new weather station
 */
export const createWeatherStation = async (data: WeatherStationCreate): Promise<WeatherStation> => {
  return apiClient.post<WeatherStation>('/weather-stations', data);
};

/**
 * Update an existing weather station
 */
export const updateWeatherStation = async (
  stationId: string,
  data: WeatherStationUpdate
): Promise<WeatherStation> => {
  return apiClient.put<WeatherStation>(`/weather-stations/${stationId}`, data);
};

/**
 * Delete a weather station
 */
export const deleteWeatherStation = async (stationId: string): Promise<{ success: boolean }> => {
  return apiClient.delete<{ success: boolean }>(`/weather-stations/${stationId}`);
};

/**
 * Get weather data from a weather station
 */
export const getWeatherStationData = async (stationId: string): Promise<WeatherDataResponse> => {
  return apiClient.get<WeatherDataResponse>(`/weather-stations/${stationId}/data`);
};

// ============================================================================
// Geo Import API
// ============================================================================

/**
 * Preview GeoPackage/JSON data before import
 */
export const previewGeoData = async (formData: FormData): Promise<GeoJSON.FeatureCollection> => {
  const authHeaders = apiClient.getAuthHeaders();
  
  const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'}/geo/preview`, {
    method: 'POST',
    headers: {
      ...authHeaders,
    },
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Import GeoPackage/JSON data
 */
export const importGeoData = async (formData: FormData): Promise<GeoImportResult> => {
  const authHeaders = apiClient.getAuthHeaders();
  
  const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'}/geo/import`, {
    method: 'POST',
    headers: {
      ...authHeaders,
    },
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

// ============================================================================
// Export all API functions
// ============================================================================

const farmApi = {
  // Farms
  getFarms,
  getFarmOptions,
  getFarm,
  createFarm,
  updateFarm,
  deleteFarm,
  discardDraftFarm,
  getFarmStats,
  getFarmWeather,
  getFarmWeatherPage,
  getAllTimezones,
  searchTimezones,

  // Fields
  getFields,
  getField,
  createField,
  updateField,
  deleteField,
  getFieldGeometry,
  getFieldSummary,

  // Irrigation Sectors
  getIrrigationSectors,
  getIrrigationSectorsByFarm,
  getIrrigationSector,
  createIrrigationSector,
  updateIrrigationSector,
  deleteIrrigationSector,
  getIrrigationSectorGeometry,
  calculateIrrigationRequirement,

  // Pump Stations
  getPumpStations,
  getPumpStation,
  createPumpStation,
  updatePumpStation,
  deletePumpStation,

  // Weather Stations
  getWeatherStations,
  getWeatherStation,
  createWeatherStation,
  updateWeatherStation,
  deleteWeatherStation,
  getWeatherStationData,

  // Geo Import
  previewGeoData,
  importGeoData,

  // Geo Export
  exportFarmGeoPackage,

  // Farm Sector Defaults
  getFarmSectorDefaults,
  updateFarmSectorDefaults,
};

export default farmApi;
